import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const URL_RE = /(?:https?:\/\/|www\.)[^\s<>"']+/i;

export function firstUrl(text) {
  const m = String(text || "").match(URL_RE);
  if (!m) return null;
  const u = m[0].replace(/[.,;:!?)"']+$/, "");
  return /^https?:/i.test(u) ? u : `https://${u}`;
}

/** Watches `text` for the first URL and fetches its Open Graph preview (debounced). */
export default function useLinkPreview(text, { enabled = true, delay = 600 } = {}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissedUrl, setDismissedUrl] = useState(null);
  const lastUrl = useRef(null);
  const url = enabled ? firstUrl(text) : null;

  useEffect(() => {
    if (!url) { lastUrl.current = null; setPreview(null); setLoading(false); return; }
    if (url === dismissedUrl) { setPreview(null); return; }
    if (url === lastUrl.current) return;
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/link-preview`, { params: { url } });
        if (alive) { lastUrl.current = url; setPreview(r.data); }
      } catch {
        if (alive) { lastUrl.current = url; setPreview(null); }
      } finally {
        if (alive) setLoading(false);
      }
    }, delay);
    return () => { alive = false; clearTimeout(t); };
  }, [url, dismissedUrl, delay]);

  const dismiss = () => { setDismissedUrl(url); setPreview(null); };
  const reset = () => { setPreview(null); setDismissedUrl(null); lastUrl.current = null; };
  return { url, preview, loading, dismissed: !!url && url === dismissedUrl, dismiss, reset };
}
